import React, {Component} from 'react';
import {FormHelperText} from '@material-ui/core';
import {Stripe, loadStripe, StripeElements, StripeCardElementChangeEvent} from '@stripe/stripe-js';
import {CardElement, ElementsConsumer, Elements} from '@stripe/react-stripe-js';
import config from 'config';
import {startCardSetup} from 'services/payment';
import {ValidatedFieldProps, ValidatedFieldState} from 'components/forms/validation';
import './index.scss';

let _stripePromise: Promise<Stripe|null>|null = null;

function loadStripeAPI(): Promise<Stripe|null> {
  if(!_stripePromise) {
    _stripePromise = loadStripe(config.stripe.apiKey);
  }
  return _stripePromise;
}

interface PaymentMethodFieldProps extends ValidatedFieldProps {
  onValidChanged?: (valid:boolean)=>void;
  onInput?: ()=>void;
  name?: string;
  disabled?: boolean;
}

export default class PaymentMethodField extends Component<PaymentMethodFieldProps, ValidatedFieldState> {
  state = {
    dirty: false,
    touched: false,
    validationMessage: undefined
  };

  private _stripe: Stripe|null = null;
  private _elements: StripeElements|null = null;

  componentDidMount(): void {
    if(this.props.validationGroup && this.props.name) {
      this.props.validationGroup.addFieldListener(this.props.name, this.onValidChanged);
    }
  }

  componentWillUnmount(): void {
    if(this.props.validationGroup && this.props.name) {
      this.props.validationGroup.removeFieldListener(this.props.name, this.onValidChanged);
    }
  }

  onValidChanged = (errors: string[], forceDirty: boolean) => {
    const touched = this.state.touched || forceDirty;
    const dirty = this.state.dirty || forceDirty;
    if(errors) {
      let msg = this.props.validationMessages ? this.props.validationMessages[errors[0]] : null;
      if(!msg) {
        msg = errors[0];
      }
      this.setState({ validationMessage: msg, dirty: dirty, touched: touched });
    } else {
      this.setState({ validationMessage: undefined, dirty: dirty, touched: touched });
    }
  };

  public async setupPaymentMethod(): Promise<string> {
    if(!this._stripe || !this._elements) {
      throw new Error("Stripe not initialized");
    }
    const clientSecret = await startCardSetup();
    const result = await this._stripe.confirmCardSetup(clientSecret, { payment_method: { card: this._elements.getElement(CardElement)! } });
    if(result.error || !result.setupIntent || !result.setupIntent.payment_method) {
      console.warn('Failed creating setup intent', result.error);
    }
    return result.setupIntent!.payment_method!;
  }

  onStripeInitialized(stripe: Stripe|null, elements: StripeElements|null) {
    this._stripe = stripe;
    this._elements = elements;
  }

  onCardChange = (ev: StripeCardElementChangeEvent) => {
    const value = ev.complete;
    this.setState({dirty: true}, () => {
      if(this.props.model && this.props.name) {
        this.props.model[this.props.name] = value;
      }
      if(this.props.validationGroup && this.props.name) {
        this.props.validationGroup.validateField(this.props.name);
      }
    });
    if(this.props.onValidChanged) {
      this.props.onValidChanged(ev.complete);
    }
    if(this.props.onInput) {
      this.props.onInput();
    }
  };

  onCardBlur = () => {
    this.setState({touched: true});
  };

  render() {
    const showError = this.state.touched && this.state.dirty && !!this.state.validationMessage;
    const className = this.props.disabled ? 'payment-method-field disabled' : 'payment-method-field';
    return <div className={className}>
      <Elements stripe={loadStripeAPI()}>
        <ElementsConsumer>
          {({elements, stripe}) => {
            this.onStripeInitialized(stripe, elements);
            return <CardElement onBlur={this.onCardBlur} onChange={this.onCardChange} className="card-field" />;
          }}
        </ElementsConsumer>
      </Elements>
      { showError && <FormHelperText className="Mui-error">{this.state.validationMessage}</FormHelperText> }
    </div>;
  }
}